import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconMemo, IconPresentationProps } from "@insite/mobius/Icon";
import Edit from "@insite/mobius/Icons/Edit";
import Plus from "@insite/mobius/Icons/Plus";
import Modal from "@insite/mobius/Modal";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import CustomerPartNumberModal from "@thos/CustomComponents/MyPartNumbers/CustomerPartNumberModal";
import { CustomerPartNumber } from "@thos/Services/ThosApiModels";
import CustomApplicationState from "@thos/Store/CustomApplicationState";
import createCustomerPartNumber from "@thos/Store/Data/CustomerPartNumbers/Handlers/CreateCustomerPartNumber";
import clearSearch from "@thos/Store/Pages/MyPartNumbers/Handlers/ClearSearch";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    includeAccountMenu = "includeAccountMenu",
}

const mapStateToProps = (state: CustomApplicationState) => {
    return {
        getCustomerPartNumbersParameter: state.ThosState.myPartNumbers.getCustomerPartNumbersParameter,
        currentUserIsGuest: getCurrentUserIsGuest(state),
        userName: state.context.session.userName,
    };
};

const mapDispatchToProps = {
    createCustomerPartNumber,
    clearSearch,
};
interface State {
    customerPartNumber: CustomerPartNumber;
    customerPartNumbers: string[];
    selectedProductId?: string;
    selectedProduct?: ProductModel;
}

interface OwnProps extends WidgetProps {
    productContext: ProductContextModel;
    productNumber: string;
    customerProductNumber?: string[];
    manufacturerItem?: string;
    showCustomerName?: boolean;
    showLabel?: boolean;
    showManufacturerItem?: boolean;
    fields: {
        [fields.includeAccountMenu]: boolean;
    };
}

type Props = OwnProps &
    WidgetProps &
    HasProduct &
    ResolveThunks<typeof mapDispatchToProps> &
    ReturnType<typeof mapStateToProps>;

export interface ProductDetailsCustomerPartNumberStyles {
    labelText?: TypographyProps;
    labelTextValue?: TypographyProps;
    valueText?: TypographyProps;
    editCartLineGridItem?: GridItemProps;
    editCartLineIcon?: IconPresentationProps;
    addCartLineGridItem?: GridItemProps;
    addCartLineIcon?: IconPresentationProps;
    addCPN?: ClickablePresentationProps;
}

export const customerPartNumberStyles: ProductDetailsCustomerPartNumberStyles = {
    labelText: {
        size: 16,
        weight: "normal",
        css: css`
            margin-right: 15px;
            display: block;
        `,
    },
    labelTextValue: {
        size: 16,
        weight: "bold",
    },
    valueText: {
        size: 16,
    },
    editCartLineGridItem: {
        css: css`
            display: flex;
            justify-content: flex-start;
            align-items: end;
        `,
    },
    editCartLineIcon: {
        src: Edit,
        color: "#D18A44",
        css: css`
            margin-left: 10px;
            width: 20px;
        `,
    },
    addCartLineGridItem: {
        css: css`
            justify-content: left;
        `,
        width: [12, 12, 12, 11, 10],
    },
    addCartLineIcon: {
        src: Plus,
        color: "#D18A44",
        css: css`
            width: 18px;
            margin-left: 9px;
            &:hover {
                color: rgb(156, 97, 38);
            }
        `,
    },
    addCPN: {
        css: css`
            color: rgb(209, 138, 68);
            font-size: 15px;
            font-weight: 700;
            // margin-right: 1em;
            &:hover {
                color: rgb(156, 97, 38);
            }
        `,
    },
};

const styles = customerPartNumberStyles;

const ProductDetailsCustomerPartNumber: React.FC<Props> = ({
    product,
    customerProductNumber,
    currentUserIsGuest,
    userName,
}) => {
    const defaultCustomerPartNumberState = {
        id: "",
        product,
        customerPartNumbers: customerProductNumber,
    } as any;
    const [modalIsOpen, setModalIsOpen] = React.useState(false);
    const [newCustomerPartNumber, setNewCustomerPartNumber] = React.useState(defaultCustomerPartNumberState);

    React.useEffect(() => {
        const currentCustomerProductState = {
            id: "",
            product,
            customerPartNumbers: customerProductNumber,
        } as any;
        setNewCustomerPartNumber(currentCustomerProductState)
    }, [product.id])

    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };

    const editClickHandler = () => {
        setModalIsOpen(true);
    };

    const formCancelHandler = (e: any) => {
        setNewCustomerPartNumber(defaultCustomerPartNumberState);
        e.preventDefault();
        modalCloseHandler();
    };

    const formSubmitHandler = (e: any, customerPartNumber: CustomerPartNumber) => {
        clearSearch();
        modalCloseHandler();
    };
    const includeAccount=fields.includeAccountMenu ?? false;

    if (!product.customerProductNumber && includeAccount && userName && !currentUserIsGuest) {
        return (
            <GridContainer>
                <GridItem {...styles.addCartLineGridItem}>
                    <Clickable onClick={editClickHandler} data-test-selector="partNumber_addLine" {...styles.addCPN}>
                        <VisuallyHidden>{translate("Edit item")}</VisuallyHidden>
                        {translate("Add CPN")}
                        <IconMemo {...styles.addCartLineIcon} />
                    </Clickable>
                    <Modal headline={translate("Add CPN")} isOpen={modalIsOpen} handleClose={modalCloseHandler}>
                        <CustomerPartNumberModal
                            formType="create"
                            customerPartNumber={newCustomerPartNumber}
                            onSubmit={formSubmitHandler}
                            onCancel={formCancelHandler}
                        />
                    </Modal>
                </GridItem>
            </GridContainer>
        );
    }

    if (product.customerProductNumber && includeAccount && userName && !currentUserIsGuest) {
        return (
            <StyledWrapper {...styles.editCartLineGridItem}>
                <Typography {...styles.valueText} as="p" data-test-selector="customerProductNumber">
                    <Typography {...styles.labelText}>{translate("My Part #")}</Typography>
                    <Typography {...styles.labelTextValue}>{product.customerProductNumber}</Typography>
                </Typography>
                <Clickable onClick={editClickHandler} data-test-selector="partNumber_editLine">
                    <VisuallyHidden>{translate("Edit item")}</VisuallyHidden>
                    <IconMemo {...styles.editCartLineIcon} />
                </Clickable>
                <Modal headline={translate("Edit CPN")} isOpen={modalIsOpen} handleClose={modalCloseHandler}>
                    <CustomerPartNumberModal
                        formType="update"
                        customerPartNumber={newCustomerPartNumber}
                        onSubmit={formSubmitHandler}
                        onCancel={formCancelHandler}
                    />
                </Modal>
            </StyledWrapper>
        );
    }
    return null;
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withProduct(ProductDetailsCustomerPartNumber)),
    definition: {
        displayName: "Customer Part Number",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
